import { StandBase } from './standBase';
import { Utils } from '../../core/services/utils.service';
import { Equipment } from './equipment';
import moment from 'moment';
import { IPlanogramPlanData } from '../../planogram/models/planogramData';
import { IPlanPlanogramGroup } from '../../core/models/planogramGroups';

const TIME_FORMATTER: string = 'DD.MM.YYYY';

export const enum StandType {
	BASIC = 'basic',
	ADDITIONAL = 'additional'
}

export class Stand extends StandBase {
	color: string;
	instrument: Equipment;
	categories: Array<number>;
	number: string = null;
	group_id: number;
	id: number = null;
	connectionId: number = null;
	planogramStand: Array<IPlanogramPlanData> = [];
	old_stand_id: number = null;
	tooltip: string = '';
	main_stand: number;
	active: boolean = false;
	locked: boolean;
	type: StandType = StandType.BASIC;
	library: any[];
	planogram_groups?: Array<IPlanPlanogramGroup>;
	update_date: string = '';
	scheduled_date: string = '';
	image: string;
	was_sent: boolean = false;
	selected?: boolean;

	constructor(
		x: number,
		y: number,
		instrument: Equipment,
		locked: boolean,
		categories?: Array<number>,
		angle?: number,
		name?: string,
		number?: string,
		main_stand?: number,
		type?: StandType,
		scheduled_date?: string
	) {
		super(x, y, instrument.width, instrument.height, angle, name, instrument.points);

		this.locked = locked;
		this.color = instrument.color;
		this.instrument = instrument;
		this.categories = categories || [];

		if (name != null) this.name = name; else this.name = '';
		if (angle != null) this.angle = angle; else this.angle = 0;
		if (number != null) this.number = number;
		if (main_stand != null) this.main_stand = main_stand;
		if (type != null) this.type = type;
		if (scheduled_date != null) this.scheduled_date = scheduled_date;
	}

	set style_conf(params) {
		for (let key in params) {
			this[key] = params[key];
		}
	}

	set date(datestring) {
		this.update_date = moment(datestring).format(TIME_FORMATTER);
	}

	points() {
		const stands = this.instrument.points.sort((a, b) => a.begin.x - b.begin.y),
			firstStand = stands[0],
			lastStand = stands[stands.length - 1],
			isTrapeze = firstStand.points[0].x !== firstStand.points[3].x && lastStand.points[0].x == lastStand.points[3].x,
			groupHeight = Math.max(...this.instrument.points.map(i => i.points[3].y)),
			points = [
				{
					x: isTrapeze
						? firstStand.points[0].x + lastStand.begin.x + firstStand.points[0].x
						: lastStand.begin.x + lastStand.points[0].x,
					y: firstStand.points[0].y
				},
				lastStand.points[1],
				{
					x: lastStand.points[2].x,
					y: groupHeight
				},
				{
					x: isTrapeze
						? firstStand.points[3].x + lastStand.begin.x + firstStand.points[3].x
						: lastStand.begin.x + lastStand.points[3].x,
					y: groupHeight
				}
			];

		return points.map(p => this.rotatePoint(p.x + this.x, p.y + this.y));
	}

	copy(): Stand {
		const s = new Stand(this.x, this.y, this.instrument, this.locked, this.categories, this.angle, this.name, this.number);
		s.id = this.id;
		s.fontSize = this.fontSize;
		s.colorBorder = this.colorBorder;
		s.fontColor = this.fontColor;
		s.backgroundColor = this.backgroundColor;
		s.connectionId = this.connectionId;
		s.planogramStand = [...(this.planogramStand ?? [])];
		s.group_id = this.group_id;
		s.old_stand_id = this.old_stand_id;
		s.main_stand = this.main_stand;
		s.type = this.type;
		s.update_date = this.update_date;
		s.scheduled_date = this.scheduled_date;
		s.was_sent = this.was_sent;
		if (this.library) s.library = [...this.library];
		s.planogram_groups = Array.isArray(this.planogram_groups)
			? this.planogram_groups.map((group: IPlanPlanogramGroup) => ({
				...group,
				planograms: [...(group.planograms || [])]
			}))
			: [];
		if (this.image) s.image = this.image;
		s.x = this.x;
		s.y = this.y;

		return s;
	}

	isEqual(stand: Stand): boolean {
		return this.isEqualParams(stand) && this.isEqualPosition(stand);
	}

	isEqualParams(stand: Stand): boolean {
		if (stand.width != this.width) return false;
		if (stand.depth != this.depth) return false;
		if (stand.number != this.number) return false;
		if (stand.name != this.name) return false;
		if (stand.colorBorder != this.colorBorder) return false;
		if (stand.fontSize != this.fontSize) return false;
		if (stand.fontColor != this.fontColor) return false;
		if (stand.backgroundColor != this.backgroundColor) return false;
		if (stand.type != this.type) return false;
		if (stand.categories.sort().join(',') != this.categories.sort().join(',')) return false;
		return true;
	}

	isEqualPosition(stand: Stand): boolean {
		if (stand.x != this.x) return false;
		if (stand.y != this.y) return false;
		if (stand.angle != this.angle) return false;
		return true;
	}

	nameWithNumber() {
		if (this.number) return `${this.number} | ${this.name}`;
		return this.name;
	}

	getLinearSize(as: string, params: any) {
		switch (as) {
			case 'pallet':
				let offsetDict: any = this.getOffset(params[0], params[1].filter(e => e != this), params[2]);
				let width = 2, depth = 2;
				if (offsetDict.left.draw && StandBase.distance(
					offsetDict.left.begin,
					offsetDict.left.end
				) <= 0.01) {
					depth--;
				}
				if (offsetDict.right.draw && StandBase.distance(
					offsetDict.right.begin,
					offsetDict.right.end
				) <= 0.01) {
					depth--;
				}
				if (offsetDict.bottom.draw && StandBase.distance(
					offsetDict.bottom.begin,
					offsetDict.bottom.end
				) <= 0.01) {
					width--;
				}
				if (offsetDict.top.draw && StandBase.distance(
					offsetDict.top.begin,
					offsetDict.top.end
				) <= 0.01) {
					width--;
				}

				return Utils.round(this.width * width + this.depth * depth);
		}
	}
}